'use client';

import { datadogRum } from '@datadog/browser-rum';
import {
  basicLogger,
  LDEvaluationDetail,
  LDFlagSet,
  LDInspectionFlagUsedHandler,
  LDOptions,
  LDProvider,
} from 'launchdarkly-react-client-sdk';
import { ReactNode, useMemo } from 'react';

import { Experiment } from '@/consts/LDFlags';
import { stripNulls } from '@/helpers/formatters';
import { AffiliateId, FunnelType, getAffiliateName, LDUser, VisitorCookie } from '@/models';

import { ExperimentsProvider } from './ExperimentsProvider';

const clientSideID = process.env.NEXT_PUBLIC_LD_CLIENT_ID!;
const logger = basicLogger({ level: 'warn' });

const dataDogInspector: LDInspectionFlagUsedHandler = {
  type: 'flag-used',
  name: 'dd-inspector',
  method: (key: string, detail: LDEvaluationDetail) => {
    datadogRum.addFeatureFlagEvaluation(key, detail.value);
  },
};

type Props = {
  children: ReactNode;
  initialExperiments?: Experiment[];
  initialFlags: LDFlagSet | undefined;
  visitor: VisitorCookie | undefined;
};

export const LaunchDarklyProvider = ({ children, initialExperiments = [], initialFlags, visitor }: Props) => {
  const ldContext: LDUser = useMemo(() => {
    const { vid, meta_values } = visitor ?? {};
    const { affiliate_id, source, utm_source } = meta_values ?? {};

    const userId = visitor?.isBot ? 'bot' : vid;
    const anonymous = visitor?.isBot || !vid;
    const isTester = !anonymous && (affiliate_id === AffiliateId.TEST || utm_source === 'test');

    return stripNulls({
      affiliate_id,
      affiliate_name: getAffiliateName(affiliate_id),
      anonymous,
      funnel_type: source ?? FunnelType.MP,
      isTester,
      key: userId || 'unknown',
      kind: 'user',
      utm_source,
    });
  }, [visitor]);

  const ldOptions: LDOptions = useMemo(
    () => ({ bootstrap: initialFlags, evaluationReasons: true, inspectors: [dataDogInspector], logger }),
    [initialFlags]
  );

  return (
    <LDProvider clientSideID={clientSideID} context={ldContext} options={ldOptions} timeout={0.5}>
      <ExperimentsProvider initialValue={initialExperiments}>{children}</ExperimentsProvider>
    </LDProvider>
  );
};
